import React from "react";
import { useEvent } from "../../../../context/EventContext";
import Select from "react-select"; // Import react-select
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Loader from "../../../Loader/Loader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import eventsAPI from "../../../../api/eventsAPI";

//  * SubEventForm is a form component that allows users to create and edit sub-events in the organizer dashboard.
//  * The form includes fields for sub-event name, type, start and end dates, start and end times, description, speakers, sponsors, and registration type.
//  * The form also includes a multi-select dropdown for selecting multiple speakers, sponsors, and registration types.
//  * The form is used in the organizer dashboard to add and update sub-events for the main event.
const SubEventForm = ({ setIsFormVisible }) => {
  const {
    subEventFormData,
    handleSubChange,
    locations,
    speakers,
    sponsors,
    types,
    setSubEventFormData,
    formData,
    loading,
    setSubEvents,
    mainEventID,
    subEventEditIndex,
    setLoading,
    setSubEventAdded,
    subEvents,
  } = useEvent();

  // Sample options for multiselect

  const locationOptions = locations.map((location) => ({
    value: location._id,
    label: location.space_name,
  }));

  const speakerOptions = speakers.map((speaker) => ({
    value: speaker._id,
    label: speaker.speaker_name,
  }));

  const sponsorOptions = sponsors.map((sponsor) => ({
    value: sponsor._id,
    label: sponsor.company_name,
  }));

  const registrationTypeOptions = types.map((type) => ({
    value: type._id,
    label: type.typeName,
  }));

  //   console.log(mainEventID);

  //   Resets the sub event form data to its initial state.
  const resetFormData = () => {
    setSubEventFormData({
      title: "",
      event_type: "",
      location: "",
      start: null,
      end: null,
      description: "",
      speakers: [],
      sponsors: [],
      registration_types: [],
      isOpen: "",
      mainEventID: mainEventID,
    });
  };

  //   * Handles the sub event form submission by sending a POST request to the organizer/subevent endpoint.
  //   * If successful, it adds the sub event to the database and shows a success message.
  //   * If the request fails, it shows an error message.
  //   * It also updates the local state by adding the new sub event and resetting the form data.
  //   * If the sub event is edited, it updates the local state by replacing the edited sub event with the new data.
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post("/organizer/subevent", {
        ...subEventFormData,
        mainEventID,
      });
      if (response.status === 200) {
        toast.success("SubEvent added successfully!");
        setLoading(false);
        setIsFormVisible(false);
        resetFormData();

        // Check if event_name is defined and not empty
        const eventName = subEventFormData?.title || "Untitled Event"; // Fallback title

        setSubEventFormData({
          ...subEventFormData,
          title: eventName,
        });

        // setAvailableViews(["month"]);
        setSubEventAdded(true);

        if (subEventEditIndex !== null) {
          const updatedData = subEvents.map((subEvent, index) =>
            index === subEventEditIndex ? response.data : subEvent
          );
          setSubEvents(updatedData);
        } else {
          setSubEvents([...subEvents, response.data]);
        }
      }
    } catch (error) {
      console.log("Error creating subEvent:", error);
      setLoading(false);
    }
  };

  //   * Handles the update of a sub event by sending a POST request to the /organizer/updateSubEvent endpoint.
  //   * If successful, it updates the local state with the updated sub event data and shows a success message.
  //   * If the request fails, it shows an error message.
  //   * It also updates the local state by updating the title, start, and end dates of the sub event in the form data.
  //   * This function is used to update the sub event details in the database when the user edits a sub event.
  const handleUpdate = async (e) => {
    // e.preventDefault(); // Uncomment if using form submission

    setLoading(true);

    try {
      const response = await eventsAPI.post(
        "/organizer/updateSubEvent",
        subEventFormData
      );

      if (response.status === 200) {
        toast.success("Sub Event updated successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Update the local state with the updated SubEvent data
        const updatedSubEvents = subEvents.map((subEvent, index) =>
          index === subEventEditIndex ? response.data : subEvent
        );
        setSubEvents(updatedSubEvents);

        // Now update the calendar view
        const updatedEventName = subEventFormData?.title || "Untitled Event"; // Ensure fallback title

        setSubEventFormData({
          ...subEventFormData,
          title: updatedEventName, // Update the title in the form data
          start: new Date(subEventFormData?.start_date), // Ensure the start date is updated
          end: new Date(subEventFormData?.end_date), // Ensure the end date is updated
        });
      }
    } catch (error) {
      console.log("Error updating subEvents:", error);
      setLoading(false);
    }
  };

  //   * Handles the deletion of a subEvent by sending a DELETE request to the organizer/subevent/{id} endpoint.
  //   * If successful, it removes the subEvent from the database and shows a success message.
  //   * If the request fails, it shows an error message.
  //   * It also updates the local state by removing the deleted subEvent and resetting the added state if there are no subEvents left.
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(`/organizer/subevent/${id}`);

      if (response.status === 200) {
        toast.success("subEvent deleted successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Remove the deleted subEvent from the local state
        const updatedsubEvent = subEvents.filter(
          (subEvent) => subEvent._id !== id
        );
        setSubEvents(updatedsubEvent);

        // Check if there are any subEvents left
        if (updatedsubEvent.length === 0) {
          setSubEventAdded(false); // Reset the added state to show the "Add Sub-Event" button
        }
      }
    } catch (error) {
      console.log("Error deleting subEvent:", error);
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSave}>
        <h2>Sub-Event Details</h2>

        {/* Event Name */}
        <label htmlFor="space-name">Event Name</label>
        <input
          name="title"
          type="text"
          placeholder="Enter the name of your event"
          value={subEventFormData.title}
          onChange={handleSubChange}
        />

        {/* Event Type */}
        <label htmlFor="event-type" className="event-name">
          Event Type
        </label>
        <select
          name="event_type"
          value={subEventFormData.event_type}
          onChange={handleSubChange}
        >
          <option value="select">Select</option>
          <option value="company retreats">Company retreats</option>
          <option value="tradeshows">Tradeshows</option>
          <option value="product launches">Product launches</option>
          <option value="conferences">Conferences</option>
          <option value="seminars">Seminars</option>
          <option value="trainings">Trainings</option>
          <option value="charity events">Charity events</option>
          <option value="hackathons">Hackathons</option>
          <option value="job fairs">Job fairs</option>
        </select>

        {/* select location */}
        <label htmlFor="start_time">Select Location</label>
        <Select
          isSearchable
          name="location"
          options={locationOptions}
          value={locationOptions.find(
            (option) => option.value === subEventFormData.location
          )}
          onChange={(selected) =>
            setSubEventFormData((prevData) => ({
              ...prevData,
              location: selected.value,
            }))
          }
        />

        {/* Start Date and Time */}
        <div className="date-section">
          <div className="start-date-time">
            <label htmlFor="start_date" className="date-picker-label">
              Start <span className="//required">*</span>
            </label>
            <div className="date-picker-wrapper">
              <DatePicker
                name="start_date"
                id="start-date"
                showIcon
                dateFormat="dd/MM/yyyy hh:mm aa" // Adjust date and time format as needed
                placeholderText="DD/MM/YY HH:MM AM/PM"
                className="date-picker-input"
                selected={
                  subEventFormData.start instanceof Date
                    ? subEventFormData.start
                    : new Date()
                }
                onChange={(date) => {
                  if (date instanceof Date && !isNaN(date)) {
                    setSubEventFormData((prevData) => ({
                      ...prevData,
                      start: date, // Ensure it's a valid Date object
                    }));
                  } else {
                    console.error("Invalid date selected");
                  }
                }}
                minDate={formData.start_date}
                maxDate={formData.end_date}
                showTimeSelect // Enable time selection
                timeIntervals={30} // Time intervals (30 minutes in this case)
                timeCaption="Time" // Caption for the time selection dropdown
              />
            </div>
          </div>

          {/* End Date and Time */}
          <div className="start-date-time">
            <label htmlFor="start_date" className="date-picker-label">
              End <span className="//required">*</span>
            </label>
            <div className="date-picker-wrapper">
              <DatePicker
                name="end_date"
                id="start-date"
                showIcon
                dateFormat="dd/MM/yyyy hh:mm aa" // Adjust date and time format as needed
                placeholderText="DD/MM/YY HH:MM AM/PM"
                className="date-picker-input"
                selected={
                  subEventFormData.end instanceof Date
                    ? subEventFormData.end
                    : new Date()
                }
                onChange={(date) => {
                  if (date instanceof Date && !isNaN(date)) {
                    setSubEventFormData((prevData) => ({
                      ...prevData,
                      end: date,
                    }));
                  } else {
                    console.log("Invalid date selected");
                  }
                }}
                minDate={formData.start_date}
                maxDate={formData.end_date}
                showTimeSelect // Enable time selection
                timeIntervals={30} // Time intervals (30 minutes in this case)
                timeCaption="Time" // Caption for the time selection dropdown
              />
            </div>
          </div>
        </div>
        <div>
          <label>Description</label>
          <textarea
            name="description"
            rows={6}
            cols={50}
            placeholder="Enter the details of your event"
            value={subEventFormData.description}
            onChange={handleSubChange}
          ></textarea>
        </div>

        {/* Select Multiple Speakers */}
        <label htmlFor="speakers">Select Speakers</label>
        <Select
          isMulti
          isSearchable
          name="sub_event_speakers"
          options={speakerOptions}
          value={speakerOptions.filter((speaker) =>
            subEventFormData?.speakers.includes(speaker.value)
          )}
          onChange={(selected) =>
            setSubEventFormData((prevData) => ({
              ...prevData,
              speakers: selected.map((option) => option.value),
            }))
          }
        />

        {/* Select Multiple Sponsors */}
        <label htmlFor="sponsors">Select Sponsors</label>
        <Select
          isMulti
          isSearchable
          name="sub_event_sponsors"
          options={sponsorOptions}
          // value={selectedSponsors}
          // onChange={(selected) => setSelectedSponsors(selected)}
          value={sponsorOptions.filter((sponsor) =>
            subEventFormData?.sponsors.includes(sponsor.value)
          )}
          onChange={(selected) =>
            setSubEventFormData((prevData) => ({
              ...prevData,
              sponsors: selected.map((option) => option.value),
            }))
          }
        />

        {/* Select Multiple Registration Types */}
        <label htmlFor="registration_type">Select Registration Type</label>
        <Select
          isMulti
          isSearchable
          name="sub_event_registration_type"
          options={registrationTypeOptions}
          // value={selectedRegistrationTypes}
          // onChange={(selected) => setSelectedRegistrationTypes(selected)}
          value={registrationTypeOptions.filter((type) =>
            subEventFormData?.registration_types.includes(type.value)
          )}
          onChange={(selected) =>
            setSubEventFormData((prevData) => ({
              ...prevData,
              registration_types: selected.map((option) => option.value),
            }))
          }
        />

        {/* Is Open? */}
        <label>Is Open?</label>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <label htmlFor="open-yes">
            <input
              type="radio"
              id="open-yes"
              name="isOpen"
              value="yes"
              // checked={isOpen === "yes"}
              // onChange={() => setIsOpen("yes")}
              checked={subEventFormData.isOpen === true}
              onChange={() =>
                setSubEventFormData((prevData) => ({
                  ...prevData,
                  isOpen: true,
                }))
              }
            />
            Yes
          </label>
          <label htmlFor="open-no">
            <input
              type="radio"
              id="open-no"
              name="isOpen"
              value="no"
              // checked={isOpen === "no"}
              // onChange={() => setIsOpen("no")}
              checked={subEventFormData.isOpen === false}
              onChange={() =>
                setSubEventFormData((prevData) => ({
                  ...prevData,
                  isOpen: false,
                }))
              }
            />
            No
          </label>
        </div>

        {/* Show the "Save" button if adding a new location, and "Update" button if editing */}
        {subEventEditIndex === null ? (
          <button
            type="submit"
            className="create-button"
            // onClick={() => handleSave()}
          >
            {loading ? <Loader /> : "SAVE"}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="create-button"
              onClick={() => handleUpdate()}
            >
              {loading ? <Loader /> : "UPDATE"}
            </button>
            <button
              type="button"
              className="create-button"
              onClick={(e) => handleDelete(e, subEventFormData._id)}
            >
              {loading ? <Loader /> : "DELETE"}
            </button>
          </>
        )}
      </form>
    </>
  );
};

export default SubEventForm;
